"use client";

import { Flag } from "@/components/shared/flags";
import { Card } from "@/components/ui/card";
import { ChevronRight, HelpCircle, Trophy, Target } from "lucide-react";
import Link from "next/link";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import type { SkupinaData } from "@/app/(zoh)/skupiny/page";

const HERO_BADGE_CLASSES =
  "mb-4 bg-muted/50 text-muted-foreground border-muted/20 rounded-full px-4 py-1 text-xs font-bold uppercase tracking-widest";
const HERO_TITLE_CLASSES =
  "text-4xl md:text-6xl font-black tracking-tighter mb-6 uppercase";

interface SkupinyKlientProps {
  skupinyData: SkupinaData[];
}

export function SkupinyKlient({ skupinyData }: SkupinyKlientProps) {
  const HERO_TITLE = "Základné skupiny";
  const HERO_SUBTITLE = "ZOH 2026";
  const HERO_DESC =
    "Dvanástka najlepších svetových tímov je rozdelených do troch skupín. Slovensko obhajuje bronz a čaká ho konfrontácia v skupine B.";
  const TOURNAMENT_INFO_TITLE = "Informácie o turnaji";
  const PLAYOFF_KEY_TITLE = "Postupový kľúč do play-off";
  const CRITERIA_TITLE = "Kritériá pri rovnosti bodov";

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* Hero Section */}
      <section className="bg-muted/10 border-b border-muted/20 py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl text-center md:text-left mx-auto md:mx-0"
          >
            <Badge variant="outline" className={HERO_BADGE_CLASSES}>
              Turnajový formát
            </Badge>
            <h1 className={HERO_TITLE_CLASSES}>
              {HERO_TITLE}{" "}
              <span className="text-primary block sm:inline mt-2 sm:mt-0">
                {HERO_SUBTITLE}
              </span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground/80 font-medium leading-relaxed max-w-2xl">
              {HERO_DESC}
            </p>
          </motion.div>
        </div>
      </section>

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        {/* Skupiny s tabuľkami */}
        <div className="space-y-16 mb-24">
          {skupinyData.map((data, idx) => {
            const maSlovensko = data.tabulka.some(
              (t) => t.krajina.kod === "SVK",
            );
            const skupinaSlug = data.skupina.slug;
            const pocetOdohratych =
              data.tabulka.reduce(
                (sum, t) => sum + t.vyhry + t.prehry + t.remizy_sn,
                0,
              ) / 2;

            return (
              <motion.section
                key={data.skupina.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.15 }}
              >
                {/* Hlavička skupiny */}
                <div className="flex items-center justify-between mb-6">
                  <Link
                    href={`/skupiny/${skupinaSlug}`}
                    className="group flex items-center gap-3"
                  >
                    <h2 className="text-2xl md:text-3xl font-black tracking-tighter uppercase group-hover:text-primary transition-colors">
                      {data.skupina.nazov}
                    </h2>
                    {maSlovensko && (
                      <Badge className="bg-primary text-primary-foreground font-black uppercase tracking-widest text-[10px] px-3 py-1 rounded-full">
                        Náš tím
                      </Badge>
                    )}
                    <ChevronRight className="h-5 w-5 text-muted-foreground/30 group-hover:text-primary group-hover:translate-x-1 transition-all" />
                  </Link>
                  <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest">
                    {pocetOdohratych > 0
                      ? `${pocetOdohratych} zápas${pocetOdohratych === 1 ? "" : pocetOdohratych < 5 ? "y" : "ov"} odohraných`
                      : "Turnaj začal"}
                  </span>
                </div>

                {/* Tabuľka skupiny */}
                <Card
                  className={cn(
                    "border-muted/20 bg-transparent shadow-sm rounded-2xl overflow-hidden mb-6",
                    maSlovensko ? "border-primary/20" : "",
                  )}
                >
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-muted/30 border-b border-muted/20">
                          <th className="text-left py-3 px-4 font-black text-[10px] uppercase tracking-widest text-muted-foreground w-8">
                            #
                          </th>
                          <th className="text-left py-3 px-4 font-black text-[10px] uppercase tracking-widest text-muted-foreground">
                            Krajina
                          </th>
                          <th className="text-center py-3 px-2 font-black text-[10px] uppercase tracking-widest text-muted-foreground w-10">
                            Záp.
                          </th>
                          <th className="text-center py-3 px-2 font-black text-[10px] uppercase tracking-widest text-muted-foreground w-10">
                            Výh.
                          </th>
                          <th className="text-center py-3 px-2 font-black text-[10px] uppercase tracking-widest text-muted-foreground w-10">
                            Rem.
                          </th>
                          <th className="text-center py-3 px-2 font-black text-[10px] uppercase tracking-widest text-muted-foreground w-10">
                            Preh.
                          </th>
                          <th className="text-center py-3 px-2 font-black text-[10px] uppercase tracking-widest text-muted-foreground w-12">
                            Strel.
                          </th>
                          <th className="text-center py-3 px-2 font-black text-[10px] uppercase tracking-widest text-muted-foreground w-12">
                            Inkas.
                          </th>
                          <th className="text-center py-3 px-4 font-black text-[10px] uppercase tracking-widest text-primary w-12">
                            Body
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.tabulka.map((riadok, rIdx) => {
                          const zapasy =
                            riadok.vyhry + riadok.prehry + riadok.remizy_sn;
                          const jeSVK = riadok.krajina.kod === "SVK";
                          const jePostup = rIdx < 2;

                          return (
                            <tr
                              key={riadok.id}
                              className={cn(
                                "border-b border-muted/10 last:border-0 transition-colors hover:bg-muted/10",
                                jeSVK ? "bg-primary/5" : "",
                                jePostup
                                  ? "border-l-2 border-l-status-success"
                                  : "",
                              )}
                            >
                              <td className="py-3 px-4 font-black text-muted-foreground text-xs">
                                {rIdx + 1}
                              </td>
                              <td className="py-3 px-4">
                                <Link
                                  href={`/krajina/${riadok.krajina.kod.toLowerCase()}`}
                                  className="flex items-center gap-3 group"
                                >
                                  <Flag
                                    kod={riadok.krajina.kod}
                                    size={24}
                                    className="shadow-sm rounded"
                                  />
                                  <span
                                    className={cn(
                                      "font-bold group-hover:text-primary transition-colors",
                                      jeSVK
                                        ? "text-primary font-black"
                                        : "text-foreground",
                                    )}
                                  >
                                    {riadok.krajina.nazov}
                                  </span>
                                </Link>
                              </td>
                              <td className="py-3 px-2 text-center font-bold text-muted-foreground">
                                {zapasy}
                              </td>
                              <td className="py-3 px-2 text-center font-bold text-foreground">
                                {riadok.vyhry}
                              </td>
                              <td className="py-3 px-2 text-center font-bold text-muted-foreground">
                                {riadok.remizy_sn}
                              </td>
                              <td className="py-3 px-2 text-center font-bold text-muted-foreground">
                                {riadok.prehry}
                              </td>
                              <td className="py-3 px-2 text-center font-bold text-muted-foreground tabular-nums">
                                {riadok.skore_za}
                              </td>
                              <td className="py-3 px-2 text-center font-bold text-muted-foreground tabular-nums">
                                {riadok.skore_priem}
                              </td>
                              <td className="py-3 px-4 text-center font-black text-primary text-lg">
                                {riadok.body}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </Card>

                {/* Zápasy skupiny - kompaktný zoznam */}
                {data.zapasy.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {data.zapasy.map((zapas) => {
                      const jeUkonceny = zapas.stav === "ukonceny";
                      const datumObj = new Date(zapas.datum_cas);
                      const datumStr = datumObj.toLocaleDateString("sk-SK", {
                        day: "numeric",
                        month: "numeric",
                      });
                      const casStr = datumObj.toLocaleTimeString("sk-SK", {
                        hour: "2-digit",
                        minute: "2-digit",
                      });

                      return (
                        <Link
                          key={zapas.id}
                          href={`/zapas/${zapas.id}`}
                          className={cn(
                            "flex items-center gap-3 p-3 rounded-xl border border-muted/15 bg-background/50",
                            "hover:bg-muted/10 transition-all text-sm cursor-pointer",
                            jeUkonceny ? "border-status-success/20" : "",
                          )}
                        >
                          <div className="text-[10px] font-bold text-muted-foreground w-12 text-center shrink-0">
                            <div>{datumStr}</div>
                            <div>{casStr}</div>
                          </div>

                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <Flag kod={zapas.domaci.kod} size={18} />
                            <span
                              className={cn(
                                "font-bold truncate text-xs",
                                zapas.domaci.kod === "SVK"
                                  ? "text-primary"
                                  : "",
                              )}
                            >
                              {zapas.domaci.kod}
                            </span>
                          </div>

                          <div className="font-black text-center w-16 shrink-0">
                            {jeUkonceny ? (
                              <span className="tabular-nums">
                                {zapas.skore_domaci} : {zapas.skore_hosti}
                              </span>
                            ) : (
                              <span className="text-muted-foreground/40 text-xs">
                                vs
                              </span>
                            )}
                          </div>

                          <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
                            <span
                              className={cn(
                                "font-bold truncate text-xs",
                                zapas.hosti.kod === "SVK" ? "text-primary" : "",
                              )}
                            >
                              {zapas.hosti.kod}
                            </span>
                            <Flag kod={zapas.hosti.kod} size={18} />
                          </div>

                          <Badge
                            variant="outline"
                            className={cn(
                              "text-[8px] font-black uppercase tracking-widest rounded-full px-2 py-0.5 shrink-0",
                              jeUkonceny
                                ? "bg-status-success/10 text-status-success border-status-success/20"
                                : "bg-muted/30 text-muted-foreground border-muted/20",
                            )}
                          >
                            {jeUkonceny
                              ? "Koniec"
                              : zapas.stav === "prebieha"
                                ? "LIVE"
                                : "Plan."}
                          </Badge>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </motion.section>
            );
          })}
        </div>

        {/* Postupový kľúč */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-4 w-1 bg-status-success rounded-full" />
            <span className="text-xs font-black text-muted-foreground uppercase tracking-widest">
              Legenda
            </span>
          </div>
          <div className="flex flex-wrap gap-4 text-xs font-bold text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className="h-3 w-1 bg-status-success rounded-full" />
              <span>1.-2. miesto = postup (priamy / kvalifikácia)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-black text-primary">B</span>
              <span>= Body</span>
            </div>
            <div className="flex items-center gap-2">
              <span>V = Výhry, R = Remízy/SN, P = Prehry, Z = Zápasy</span>
            </div>
          </div>
        </div>

        {/* Rozšírená legenda a info */}
        <section className="space-y-12 max-w-4xl mx-auto">
          <div className="flex items-center justify-center md:justify-start gap-4 border-b border-muted/20 pb-6">
            <div className="p-3 bg-primary/10 rounded-2xl text-primary">
              <HelpCircle className="h-8 w-8" />
            </div>
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter uppercase">
              {TOURNAMENT_INFO_TITLE}
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div className="p-6 bg-muted/20 rounded-2xl border border-muted/20 space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-muted rounded-xl text-primary">
                  <Trophy className="h-6 w-6" />
                </div>
                <h3 className="font-black text-lg uppercase tracking-tight text-foreground">
                  {PLAYOFF_KEY_TITLE}
                </h3>
              </div>
              <div className="space-y-4 text-sm text-muted-foreground/80">
                <p className="font-medium">
                  Systém turnaja odmeňuje najlepšie tímy zo základnej časti:
                </p>
                <div className="space-y-3">
                  <div className="p-4 bg-status-success/10 border border-status-success/20 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="bg-status-success text-white font-black uppercase tracking-widest text-[10px] px-3">
                        Priamy postup
                      </Badge>
                      <span className="font-black text-foreground text-xs">
                        Štvrťfinále
                      </span>
                    </div>
                    <ul className="space-y-1 text-xs">
                      <li className="flex items-center gap-2">
                        <div className="h-1.5 w-1.5 rounded-full bg-status-success" />
                        Víťazi skupín A, B, C
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="h-1.5 w-1.5 rounded-full bg-status-success" />
                        Najlepší tím z 2. miest
                      </li>
                    </ul>
                  </div>
                  <div className="p-4 bg-muted/10 border border-muted/20 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge
                        variant="secondary"
                        className="font-black uppercase tracking-widest text-[10px] px-3"
                      >
                        Kvalifikácia
                      </Badge>
                      <span className="font-black text-foreground text-xs">
                        Osemfinále
                      </span>
                    </div>
                    <p className="text-xs">
                      Zostávajúcich 8 tímov bojuje o 4 miestenky v štvrťfinále.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 bg-muted/20 rounded-2xl border border-muted/20 space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-muted rounded-xl text-primary">
                  <Target className="h-6 w-6" />
                </div>
                <h3 className="font-black text-lg uppercase tracking-tight text-foreground">
                  {CRITERIA_TITLE}
                </h3>
              </div>
              <div className="space-y-4 text-sm text-muted-foreground/80">
                <p className="font-medium">
                  Ak majú tímy rovnaký počet bodov, poradie sa určí podľa:
                </p>
                <ol className="space-y-2">
                  {[
                    "Vzájomné zápasy medzi rovnakými tímami",
                    "Rozdiel gólov vo všetkých skupinových zápasoch",
                    "Počet strelených gólov vo všetkých skupinových zápasoch",
                    "IIHF ranking pred turnajom",
                  ].map((text, i) => (
                    <li
                      key={i}
                      className="flex items-center gap-3 bg-muted/10 p-3 rounded-lg border border-muted/10"
                    >
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-[10px] font-black shrink-0">
                        {i + 1}
                      </span>
                      <span className="text-xs font-medium">{text}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
